import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import Navbar from '../components/Navbar';
import { axiosInstance } from '../context/AuthContext';
import { FaUser, FaEnvelope, FaLock, FaCheckCircle, FaExclamationCircle, FaSpinner } from 'react-icons/fa';
import UserProfileTab from '../components/user-settings/UserProfileTab';
import MyPurchasesTab from '../components/user-settings/MyPurchasesTab';

const TABS = [
  { id: 'settings', label: 'Settings' },
  { id: 'profile', label: 'Profile' },
  { id: 'purchases', label: 'My Purchases' },
];

const UserSettings = () => {
  const { user, dispatch } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : 'settings';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsEmailVerified(user.emailVerified || false);
      // setIsEmailVerified(true);
    }
  }, [user]);

  useEffect(() => {
    if (tabFromUrl && TABS.some((t) => t.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams(tabId === 'settings' ? {} : { tab: tabId }, { replace: true });
  };

  const handleSendVerificationCode = async () => {
    setIsSendingCode(true);
    setMessage({ type: '', text: '' });
    
    try {
      const response = await axiosInstance.post('/user/email/send-verification');
      setCodeSent(true);
      setMessage({ 
        type: 'success', 
        text: response.data.message || 'Verification code sent to your email' 
      });
      
      if (response.data.code) {
        setMessage({ 
          type: 'success', 
          text: `Verification code sent! Code: ${response.data.code} (Development mode)` 
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to send verification code';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!verificationCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter the verification code' });
      return;
    }

    setIsVerifying(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axiosInstance.post('/user/email/verify', {
        code: verificationCode.trim()
      });

      const updatedUser = { ...user, ...response.data.user, emailVerified: true };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      dispatch({ type: 'LOGIN', payload: updatedUser });

      setIsEmailVerified(true);
      setMessage({ type: 'success', text: 'Email verified successfully!' });
      setVerificationCode('');
      setCodeSent(false);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to verify code';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsVerifying(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.newPassword || formData.currentPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Current password is required to change password';
      }
      if (!formData.newPassword) {
        newErrors.newPassword = 'New password is required';
      } else if (formData.newPassword.length < 8) {
        newErrors.newPassword = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
        newErrors.newPassword = 'Password must contain uppercase, lowercase, and number';
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (message.type === 'success') {
      setMessage({ type: '', text: '' });
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setMessage({ type: 'error', text: 'Please fix the errors before saving' });
      return;
    }

    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const updateData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim()
      };

      if (formData.newPassword) {
        updateData.password = formData.newPassword;
        updateData.currentPassword = formData.currentPassword;
      }

      const response = await axiosInstance.put('/user/profile/update', updateData);
      
      const updatedUser = { ...user, ...response.data.user };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      dispatch({ type: 'LOGIN', payload: updatedUser });

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to update profile. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
      
      if (error.response?.data?.field) {
        setErrors(prev => ({
          ...prev,
          [error.response.data.field]: errorMessage
        }));
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="flex">
        <Navbar />
        <main className="flex-1 ml-0 sm:ml-20  p-6">
          <h1 className="text-2xl font-semibold mb-4">User Settings</h1>
          <p>Please log in to view your settings.</p>
        </main>
      </div>
    );
  }

  if (!isEmailVerified) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Navbar />
        <main className="flex-1 ml-0 sm:ml-20 p-6 w-full">
          <div className="m-auto max-w-4xl">
            <div className="mb-6">
              <h1 className="text-3xl font-semibold text-gray-900 mb-2">Account</h1>
              <p className="text-gray-600">Manage your settings, profile, and purchases</p>
            </div>

            <div className="mb-6 border-b border-gray-200">
              <nav className="flex gap-1 -mb-px overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    className={`whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {activeTab === 'profile' && <UserProfileTab />}
            {activeTab === 'purchases' && <MyPurchasesTab />}

            {activeTab === 'settings' && (
          <div className="max-w-2xl">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Email Verification Required</h2>
              <p className="text-gray-600">Please verify your email address to access account settings</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FaEnvelope className="text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Verify Your Email</h2>
              </div>

              <p className="text-gray-700 mb-6">
                We need to verify your email address <span className="font-semibold">{user.email}</span> before you can access your settings.
              </p>

              {!codeSent ? (
                <div>
                  <button
                    onClick={handleSendVerificationCode}
                    disabled={isSendingCode}
                    className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSendingCode ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaEnvelope />
                        Send Verification Code
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleVerifyCode} className="space-y-4">
                  <div>
                    <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
                      Enter Verification Code
                    </label>
                    <input
                      type="text"
                      id="verificationCode"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="w-full rounded-md border border-gray-300 px-4 py-3 text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={6}
                      disabled={isVerifying}
                    />
                    <p className="mt-2 text-sm text-gray-500 text-center">
                      Enter the 6-digit code sent to your email
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setCodeSent(false);
                        setVerificationCode('');
                        setMessage({ type: '', text: '' });
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                      disabled={isVerifying}
                    >
                      Change Email
                    </button>
                    <button
                      type="submit"
                      disabled={isVerifying || !verificationCode.trim()}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isVerifying ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Verify Code'
                      )}
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleSendVerificationCode}
                    disabled={isSendingCode}
                    className="w-full text-sm text-blue-600 hover:text-blue-700 transition"
                  >
                    {isSendingCode ? 'Sending...' : 'Resend Code'}
                  </button>
                </form>
              )}

              {message.text && (
                <div
                  className={`mt-4 p-4 rounded-lg flex items-center gap-2 ${
                    message.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {message.type === 'success' ? (
                    <FaCheckCircle className="text-green-600" />
                  ) : (
                    <FaExclamationCircle className="text-red-600" />
                  )}
                  <span>{message.text}</span>
                </div>
              )}
            </div>
          </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Navbar />
      <main className="flex-1 ml-0 sm:ml-20  p-6 w-full">
        <div className="m-auto max-w-4xl">
          <div className="mb-6">
            <h1 className="text-3xl font-semibold text-gray-900 mb-2">Account</h1>
            <p className="text-gray-600">Manage your settings, profile, and purchases</p>
          </div>

          <div className="mb-6 border-b border-gray-200">
            <nav className="flex gap-1 -mb-px overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabChange(tab.id)}
                  className={`whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === 'profile' && <UserProfileTab />}

          {activeTab === 'purchases' && <MyPurchasesTab />}

          {activeTab === 'settings' && (
          <form onSubmit={handleSaveProfile} className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FaUser className="text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.firstName
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    disabled={isSaving}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <FaExclamationCircle className="text-xs" />
                      {errors.firstName}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.lastName
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    disabled={isSaving}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <FaExclamationCircle className="text-xs" />
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border pl-10 pr-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.email
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    disabled={isSaving}
                  />
                  {isEmailVerified && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 text-green-600">
                      <FaCheckCircle className="text-sm" />
                      <span className="text-xs font-medium">Verified</span>
                    </div>
                  )}
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <FaExclamationCircle className="text-xs" />
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <FaLock className="text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Change Password</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Leave blank if you don't want to change your password
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.currentPassword
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    disabled={isSaving}
                  />
                  {errors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <FaExclamationCircle className="text-xs" />
                      {errors.currentPassword}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.newPassword
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    disabled={isSaving}
                  />
                  {errors.newPassword && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <FaExclamationCircle className="text-xs" />
                      {errors.newPassword}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Must be at least 8 characters with uppercase, lowercase, and number
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.confirmPassword
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    disabled={isSaving}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <FaExclamationCircle className="text-xs" />
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {message.text && (
              <div
                className={`p-4 rounded-lg flex items-center gap-2 ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {message.type === 'success' ? (
                  <FaCheckCircle className="text-green-600" />
                ) : (
                  <FaExclamationCircle className="text-red-600" />
                )}
                <span>{message.text}</span>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserSettings;

